import { CheckInTicket } from "@garther/interfaces";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ticketApiClient } from "@/libs/api";

// Query keys for consistent cache management
export const ticketsKeys = {
  all: ["tickets"] as const,
  details: () => [...ticketsKeys.all, "detail"] as const,
  detail: (ticketId: string) => [...ticketsKeys.details(), ticketId] as const
};

interface UseTicketOptions {
  ticketId: string;
  enabled?: boolean;
}

export function useTicket(options: UseTicketOptions) {
  const { ticketId, enabled = true } = options;

  return useQuery({
    queryKey: ticketsKeys.detail(ticketId),
    queryFn: async () => {
      return ticketApiClient.getTicketById(ticketId);
    },
    enabled: enabled && !!ticketId,
    staleTime: 5 * 60 * 1000 // 5 minutes for individual ticket
  });
}

// Mutation hook for checking in a ticket
export function useCheckInTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CheckInTicket) => {
      return ticketApiClient.checkIn(data);
    },
    onSuccess: (data) => {
      // Invalidate the specific ticket query to refetch updated data
      queryClient.invalidateQueries({ queryKey: ticketsKeys.detail(data.id) });
      // Also invalidate all tickets to ensure consistency
      queryClient.invalidateQueries({ queryKey: ticketsKeys.all });
    }
  });
}

// Utility hook for invalidating tickets cache
export function useTicketsInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: ticketsKeys.all }),
    invalidateTicket: (ticketId: string) =>
      queryClient.invalidateQueries({ queryKey: ticketsKeys.detail(ticketId) })
  };
}
